import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { MaskLine } from "@/components/site/Reveal";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src="https://images.pexels.com/photos/36674324/pexels-photo-36674324.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="Surfer riding a wave in Agadir"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-navy" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-navy/20" />

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-20 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="label-mono text-sand mb-5"
          data-testid="hero-kicker"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="font-playfair text-white text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-tight max-w-5xl">
          <MaskLine delay={0.25}>{t.hero.line1}</MaskLine>
          <MaskLine delay={0.38} className="italic text-sand">
            {t.hero.line2}
          </MaskLine>
          <MaskLine delay={0.51}>{t.hero.line3}</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="font-manrope text-white/85 text-base md:text-lg max-w-xl mt-7"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="flex flex-wrap items-center gap-4 mt-9"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => scrollTo("booking")}
            className="bg-ocean text-white label-mono px-8 py-4 hover:bg-white hover:text-navy transition-colors duration-300"
          >
            {t.hero.cta}
          </button>
          <button
            data-testid="hero-lessons-btn"
            onClick={() => scrollTo("lessons")}
            className="border border-white/40 text-white label-mono px-8 py-4 hover:bg-white/10 transition-colors duration-300"
          >
            {t.hero.secondary}
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ opacity: { delay: 1.4 }, y: { repeat: Infinity, duration: 2 } }}
        className="absolute bottom-6 right-6 md:right-10 z-10 text-white/70"
      >
        <ArrowDown size={22} />
      </motion.div>
    </section>
  );
}
